// The Unabated Angle §F4 — derivative market pricing.
//
// Pure functions. Once the Fair Price Engine has a joint scoreline
// distribution for a fixture (src/fair-price/scoreline-grid.ts), the
// correct price for any DERIVATIVE of that market is just a different
// marginalisation of the SAME grid — no new model, no new data, no
// fabricated price. Asian handicaps (incl. quarter lines with
// stake-refund pushes), alternate totals, and first-half 1X2 (via an
// explicit, documented half-share parameter — a stated modelling
// assumption, never a hidden constant) all fall out of this.
//
// Doctrine: every function returns probabilities that the grid
// actually supports; fair odds are 1/prob and are null when the
// probability is 0 (never an invented price).

import {
  buildScorelineGrid,
  marginalize1X2,
  type ScorelineGridResult,
} from "./scoreline-grid.ts";

/** Probability distribution over the goal margin m = home − away,
 *  normalised by the grid mass (so it sums to ≈1 over the grid). */
export function marginDistribution(
  grid: ScorelineGridResult,
): Map<number, number> {
  const mass = grid.grid_mass;
  if (mass <= 0) return new Map();
  const dist = new Map<number, number>();
  for (const c of grid.cells) {
    const m = c.home - c.away;
    dist.set(m, (dist.get(m) ?? 0) + c.probability / mass);
  }
  return dist;
}

/** Probability distribution over total goals t = home + away. */
export function totalDistribution(
  grid: ScorelineGridResult,
): Map<number, number> {
  const mass = grid.grid_mass;
  if (mass <= 0) return new Map();
  const dist = new Map<number, number>();
  for (const c of grid.cells) {
    const t = c.home + c.away;
    dist.set(t, (dist.get(t) ?? 0) + c.probability / mass);
  }
  return dist;
}

export interface TwoWayPrice {
  /** Win equity for the backed side, push/refund removed. */
  readonly homeProb: number;
  readonly awayProb: number;
  /** Stake-refund (push) mass — informational; excluded from prob. */
  readonly pushProb: number;
  readonly homeFairOdds: number | null;
  readonly awayFairOdds: number | null;
}

function fairOdds(p: number): number | null {
  return p > 0 && Number.isFinite(p) ? +(1 / p).toFixed(4) : null;
}

// Settle a 1u back of the "home/over" side at additive handicap `h`
// against a key distribution: the side wins when key + h > 0, loses
// when < 0, pushes (stake refunded) when exactly 0. An integer `h`
// can push; a half-integer `h` never can.
function settleLine(
  dist: Map<number, number>,
  h: number,
): { win: number; push: number; lose: number } {
  let win = 0;
  let push = 0;
  let lose = 0;
  for (const [k, p] of dist) {
    const adj = k + h;
    if (adj > 1e-9) win += p;
    else if (adj < -1e-9) lose += p;
    else push += p;
  }
  return { win, push, lose };
}

function fracClass(x: number): number {
  const f = Math.abs(x) % 1;
  return Math.min(f, 1 - f); // 0 → integer/half ; 0.25 → quarter
}

// Conditional home-equity at a single non-quarter additive handicap
// (full push excluded + renormalised — Asian fair-odds convention).
function equityAt(
  dist: Map<number, number>,
  h: number,
): { home: number; away: number; push: number } {
  const s = settleLine(dist, h);
  const den = s.win + s.lose;
  return {
    home: den > 0 ? s.win / den : 0,
    away: den > 0 ? s.lose / den : 0,
    push: s.push,
  };
}

// Price the backed side given the additive handicap `h` (key + h > 0
// ⇒ win). Quarter lines (|frac| ≈ .25) split a 1u stake across the
// two adjacent half/whole components.
function priceFromDist(
  dist: Map<number, number>,
  h: number,
): TwoWayPrice {
  if (dist.size === 0) {
    return {
      homeProb: 0,
      awayProb: 0,
      pushProb: 0,
      homeFairOdds: null,
      awayFairOdds: null,
    };
  }
  let homeEq: number;
  let awayEq: number;
  let pushMass: number;
  if (Math.abs(fracClass(h) - 0.25) < 1e-9) {
    const a = equityAt(dist, h - 0.25);
    const b = equityAt(dist, h + 0.25);
    homeEq = 0.5 * a.home + 0.5 * b.home;
    awayEq = 0.5 * a.away + 0.5 * b.away;
    pushMass = 0; // a quarter line cannot fully push
  } else {
    const e = equityAt(dist, h);
    homeEq = e.home;
    awayEq = e.away;
    pushMass = e.push;
  }
  return {
    homeProb: +homeEq.toFixed(6),
    awayProb: +awayEq.toFixed(6),
    pushProb: +pushMass.toFixed(6),
    homeFairOdds: fairOdds(homeEq),
    awayFairOdds: fairOdds(awayEq),
  };
}

/** Asian handicap fair price. `line` is the handicap applied to the
 *  HOME side, e.g. -0.5 (home -0.5), +0.25, -1, -1.75. Quarter lines
 *  are split-ball priced; integer lines stake-refund on a push. */
export function priceAsianHandicap(
  grid: ScorelineGridResult,
  line: number,
): TwoWayPrice {
  // Home covers when (home − away) + line > 0 ⇒ additive h = line.
  return priceFromDist(marginDistribution(grid), line);
}

/** Alternate total (Over/Under `line`). homeProb = OVER, awayProb =
 *  UNDER. Quarter totals (e.g. 2.75) are split-ball priced; integer
 *  totals (e.g. 3.0) stake-refund on the exact total. */
export function priceTotalLine(
  grid: ScorelineGridResult,
  line: number,
): TwoWayPrice {
  // Over wins when total − line > 0 ⇒ additive h = −line. homeProb =
  // OVER, awayProb = UNDER. Integer totals stake-refund on t == line;
  // quarter totals (e.g. 2.75) are split-ball priced — identical
  // machinery as the handicap, just a different marginal.
  return priceFromDist(totalDistribution(grid), -line);
}

/** Default first-half goal share. A STATED modelling assumption
 *  (first halves are lower-scoring) — explicit + overridable, never a
 *  hidden fabricated constant. ~0.45 is the standard empirical share. */
export const FIRST_HALF_GOAL_SHARE = 0.45;

export interface FirstHalf1X2 {
  readonly homeWinProb: number;
  readonly drawProb: number;
  readonly awayWinProb: number;
  readonly homeFairOdds: number | null;
  readonly drawFairOdds: number | null;
  readonly awayFairOdds: number | null;
  readonly halfShare: number;
}

/** First-half 1X2 from the same engine: scale the full-match λ by an
 *  explicit half-share and re-marginalise. Independence within the
 *  half is the same Layer-1 simplification the full grid already
 *  makes — this introduces no new fabricated model. */
export function priceFirstHalf1X2(
  homeLambda: number,
  awayLambda: number,
  halfShare: number = FIRST_HALF_GOAL_SHARE,
): FirstHalf1X2 {
  const hg = buildScorelineGrid(
    Math.max(0, homeLambda) * halfShare,
    Math.max(0, awayLambda) * halfShare,
  );
  const m = marginalize1X2(hg);
  return {
    homeWinProb: +m.home_win_probability.toFixed(6),
    drawProb: +m.draw_probability.toFixed(6),
    awayWinProb: +m.away_win_probability.toFixed(6),
    homeFairOdds: fairOdds(m.home_win_probability),
    drawFairOdds: fairOdds(m.draw_probability),
    awayFairOdds: fairOdds(m.away_win_probability),
    halfShare,
  };
}
